import { API_URL } from '../../config';

/**
 * "See Kudos as someone else" (super admins). The server issues a 30-minute token
 * that names the real actor; the browser keeps the admin's own token aside and puts
 * it back when the session ends -- by choice or because time ran out -- and then tells
 * the server, with the admin's own token, so the end is in the activity log too.
 */

const ORIGINAL_TOKEN = 'pre_impersonation_token';
const TARGET = 'impersonation_target';
const EXPIRES_AT = 'impersonation_expires_at';

export type ImpersonationTarget = { id: string; name?: string; email?: string };

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function isImpersonating(): boolean {
  return Boolean(read(ORIGINAL_TOKEN));
}

export function getImpersonationTarget(): ImpersonationTarget | null {
  const raw = read(TARGET);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** When the session runs out, or null when not known (a session started before this). */
export function getImpersonationExpiry(): Date | null {
  const raw = read(EXPIRES_AT);
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Starts a session from the server's answer and opens the Dashboard as that person. */
export function startImpersonation(data: { token: string; expires_at?: string | null; user?: ImpersonationTarget }) {
  localStorage.setItem(ORIGINAL_TOKEN, localStorage.getItem('token') || '');
  localStorage.removeItem('pre_impersonation_org');
  localStorage.setItem('token', data.token);
  localStorage.removeItem('activeOrgId');
  if (data.user) localStorage.setItem(TARGET, JSON.stringify(data.user));
  else localStorage.removeItem(TARGET);
  if (data.expires_at) localStorage.setItem(EXPIRES_AT, data.expires_at);
  else localStorage.removeItem(EXPIRES_AT);
  window.location.href = '/dashboard';
}

/**
 * Back to the admin's own account. The end is recorded with the admin's token
 * (keepalive, so it is sent even as the page changes); a failure to record it never
 * keeps anyone in the session.
 */
export function endImpersonation(reason: 'ended' | 'expired' = 'ended') {
  const original = read(ORIGINAL_TOKEN);
  const target = getImpersonationTarget();
  try {
    if (original) localStorage.setItem('token', original);
    [ORIGINAL_TOKEN, TARGET, EXPIRES_AT, 'pre_impersonation_org', 'activeOrgId'].forEach((k) => localStorage.removeItem(k));
  } catch {
    // Storage unavailable: nothing to restore.
  }
  if (original && target?.id) {
    try {
      fetch(`${API_URL}/api/admin/users/${encodeURIComponent(target.id)}/impersonate/end`, {
        method: 'POST',
        keepalive: true,
        headers: { Authorization: `Bearer ${original}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      }).catch(() => {});
    } catch {
      // Recording the end is best effort.
    }
  }
  window.location.href = '/admin';
}

/** Signing out clears the session too. */
export function clearImpersonation() {
  try {
    [ORIGINAL_TOKEN, TARGET, EXPIRES_AT, 'pre_impersonation_org'].forEach((k) => localStorage.removeItem(k));
  } catch {
    // Storage unavailable.
  }
}
